// Create axios instance
import axios from "axios";
import {pb} from "@/lib/pocketbase";
import {appConfig} from "@/app-config";
import {ClientResponseError} from "pocketbase";
import {router} from "expo-router";
import * as Sentry from "@sentry/react-native";

export const api = axios.create({
    baseURL: appConfig.apiUrl, // Replace with your API server URL
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add PocketBase token
api.interceptors.request.use(
    (config) => {
        // Get the current token from PocketBase auth store
        const token = pb.authStore.token;

        if (!config.headers) {
            config.headers = {};
        }

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Optional: Add user ID to headers
        const user = pb.authStore.record;
        if (user?.id) {
            config.headers['X-User-ID'] = user.id;
        }

        return config;
    },
    (error) => {
        Sentry.captureException(error);
        console.error('❌ Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor to handle token refresh and errors
api.interceptors.response.use(
    (response) => {
        console.log(`✅ API Response: ${response.status} ${response.config.url}`);
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        console.log(`❌ API Error: ${error.response?.status} ${error.config?.url}`);

        // Check if this request should skip auth refresh (set via skipAuthRefresh flag in config)
        const skipAuthRefresh = originalRequest?.skipAuthRefresh === true;

        // Handle 401 Unauthorized errors
        if (error.response?.status === 401 && !originalRequest._retry && !skipAuthRefresh && pb.authStore.isValid) {
            originalRequest._retry = true;

            try {
                console.log('🔄 Attempting to refresh PocketBase token...');

                // Try to refresh the PocketBase token
                await pb.collection('users').authRefresh();

                // console.log('✅ Token refreshed successfully');

                // Update the Authorization header with the new token
                originalRequest.headers.Authorization = `Bearer ${pb.authStore.token}`;

                // Retry the original request
                return api(originalRequest);
            } catch (refreshError) {
                Sentry.captureException(refreshError);
                console.error('❌ Token refresh failed:', refreshError);

                // Check if it's a PocketBase ClientResponseError with 401/403
                if (refreshError instanceof ClientResponseError &&
                    (refreshError.status === 401 || refreshError.status === 403)) {
                    // Clear auth store - this triggers the onChange listener in AuthProvider
                    // which will update the user state to null
                    pb.authStore.clear();

                    console.log('🔓 Auth token invalid, redirecting to login...');

                    // Redirect to login page without throwing an error
                    // Use setTimeout to avoid navigation during request processing
                    setTimeout(() => {
                        router.replace('/');
                    }, 0);

                    // Return a rejected promise with a silent cancellation error
                    // This prevents the component from processing the request but doesn't trigger error boundaries
                    return Promise.reject(new axios.Cancel('Authentication session expired'));
                }

                // For other refresh errors (network issues, etc), reject with the original error
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);
